/* eslint-disable @typescript-eslint/no-misused-promises, no-void */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { useAuthInfo, useOrgHelper } from "@propelauth/react";
import {
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { queryClient, useWorkspaceMembers } from "../../../../hooks/QueryHooks";
import useHeaderStore from "../../../../store/layout/headerStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";

type WorkspaceMember = {
  user_id?: string;
  id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  role_name?: string;
  is_active_in_workspace?: boolean;
  user_active_status?: boolean;
  is_joined?: boolean;
  is_invited?: boolean;
  joined_at?: string;
  last_seen_at?: string;
};

type OrgPendingInvite = {
  email: string;
  role: string;
};

const normalizeEmail = (value?: string) => String(value ?? "").trim().toLowerCase();

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const parseInviteEmails = (value: string) => {
  const seen = new Set<string>();
  const valid: string[] = [];
  const invalid: string[] = [];

  value
    .split(/[\n,;]+/g)
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const key = normalizeEmail(token);
      if (!key || seen.has(key)) return;
      seen.add(key);

      if (isValidEmail(token)) valid.push(token);
      else invalid.push(token);
    });

  return { valid, invalid };
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getInitials = (member: WorkspaceMember) => {
  const first = member.first_name?.trim()?.[0] ?? "";
  const last = member.last_name?.trim()?.[0] ?? "";
  const fromName = `${first}${last}`.toUpperCase();
  if (fromName) return fromName;
  return member.email?.trim()?.[0]?.toUpperCase() ?? "";
};

const statusPill = (member: WorkspaceMember) => {
  const isPending = member.is_invited && !member.is_joined;
  if (isPending) {
    return { label: "Pending", dot: "bg-amber-500" };
  }

  const isActive = member.is_active_in_workspace ?? member.user_active_status ?? true;
  if (isActive) {
    return { label: "Active", dot: "bg-primary" };
  }

  return { label: "Inactive", dot: "bg-muted-foreground" };
};

const toPendingInviteMember = (invite: OrgPendingInvite): WorkspaceMember => ({
  id: `invite_${normalizeEmail(invite.email)}`,
  email: invite.email,
  role_name: invite.role || "Member",
  is_active_in_workspace: false,
  user_active_status: false,
  is_joined: false,
  is_invited: true,
});

const ROLE_HIERARCHY: Record<string, number> = { Owner: 3, Admin: 2, Member: 1 };
const ALL_ROLES = ["Owner", "Admin", "Member"];

const getRoleRank = (role?: string) => ROLE_HIERARCHY[role ?? ""] ?? 0;

const canManageRole = (myRole?: string, targetRole?: string) => {
  const myRank = getRoleRank(myRole);
  const targetRank = getRoleRank(targetRole);
  return myRank > targetRank;
};

const getAssignableRoles = (myRole?: string) =>
  ALL_ROLES.filter((role) => getRoleRank(myRole) >= getRoleRank(role));

type WorkspaceMembersPageProps = {
  embedded?: boolean;
};

const WorkspaceMembersPage: React.FC<WorkspaceMembersPageProps> = ({ embedded = false }) => {
  const { setHeaderContent, resetHeader } = useHeaderStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const authApis = useAuthFrontendApis();
  const { orgHelper } = useOrgHelper();
  const { user } = useAuthInfo();

  const { data: membersData, isLoading } = useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    retry: 2,
  });

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("Member");

  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<WorkspaceMember | null>(null);
  const [newRole, setNewRole] = useState<string | null>(null);

  const [revokeInviteConfirmOpen, setRevokeInviteConfirmOpen] = useState(false);
  const [pendingRevokeEmail, setPendingRevokeEmail] = useState<string | null>(null);

  const [removeMemberConfirmOpen, setRemoveMemberConfirmOpen] = useState(false);
  const [pendingRemoveMember, setPendingRemoveMember] = useState<WorkspaceMember | null>(null);

  const [propelUserIdByEmail, setPropelUserIdByEmail] = useState<Record<string, string>>({});
  const [orgPossibleRoles, setOrgPossibleRoles] = useState<string[]>([]);
  const [pendingOrgInvites, setPendingOrgInvites] = useState<OrgPendingInvite[]>([]);

  const [optimisticInvites, setOptimisticInvites] = useState<WorkspaceMember[]>([]);
  const [optimisticRemovedEmails, setOptimisticRemovedEmails] = useState<string[]>([]);
  const [optimisticRoleByEmail, setOptimisticRoleByEmail] = useState<Record<string, string>>({});

  const orgId = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return null;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.orgId ?? null;
  }, [orgHelper, workspace?.unique_identifier]);

  const currentUserRole = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return undefined;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.userAssignedRole as string | undefined;
  }, [orgHelper, workspace?.unique_identifier]);

  const currentUserEmail = normalizeEmail(user?.email);
  const canInvite = getAssignableRoles(currentUserRole).length > 0;
  const invitableRoles = useMemo(() => getAssignableRoles(currentUserRole), [currentUserRole]);

  const workspaceMembers = useMemo<WorkspaceMember[]>(
    () => (membersData?.members ?? membersData?.users ?? []) as WorkspaceMember[],
    [membersData],
  );

  const refreshOrgMembershipData = useCallback(async () => {
    if (!orgId) {
      setPropelUserIdByEmail({});
      setOrgPossibleRoles([]);
      setPendingOrgInvites([]);
      return;
    }

    try {
      const [membersResponse, pendingInvitesResponse] = await Promise.all([
        authApis.fetchOrgMembers(orgId, { page_size: 100 }),
        authApis.fetchPendingOrgInvites(orgId, { page_size: 100 }),
      ]);

      if (membersResponse.ok) {
        const mapping: Record<string, string> = {};
        const possibleRoles = new Set<string>();
        membersResponse.data.users.forEach((member: any) => {
          const emailKey = normalizeEmail(member?.email);
          if (emailKey && member?.user_id) mapping[emailKey] = String(member.user_id);
          if (member?.role) possibleRoles.add(String(member.role));
          if (Array.isArray(member?.possible_roles)) {
            member.possible_roles.forEach((role: string) => {
              if (String(role || "").trim()) possibleRoles.add(String(role));
            });
          }
        });
        setPropelUserIdByEmail(mapping);
        setOrgPossibleRoles(Array.from(possibleRoles));
      } else {
        setPropelUserIdByEmail({});
        setOrgPossibleRoles([]);
      }

      if (pendingInvitesResponse.ok) {
        const invites: OrgPendingInvite[] = (pendingInvitesResponse.data.pending_invites ?? [])
          .map((invite: any) => ({
            email: String(invite?.email ?? "").trim(),
            role: String(invite?.role ?? "").trim(),
          }))
          .filter((invite) => !!invite.email);
        setPendingOrgInvites(invites);
      } else {
        setPendingOrgInvites([]);
      }
    } catch {
      setPropelUserIdByEmail({});
      setOrgPossibleRoles([]);
      setPendingOrgInvites([]);
    }
  }, [authApis, orgId]);

  useEffect(() => {
    refreshOrgMembershipData().catch(() => {});
  }, [refreshOrgMembershipData]);

  useEffect(() => {
    if (embedded) return undefined;
    resetHeader();
    setHeaderContent({
      title: "Workspace Members",
      description: "Manage who has access to this workspace",
    });

    return () => resetHeader();
  }, [embedded, resetHeader, setHeaderContent]);

  const mergedMembers = useMemo<WorkspaceMember[]>(() => {
    const membersByEmail = new Map<string, WorkspaceMember>();

    workspaceMembers.forEach((member) => {
      const emailKey = normalizeEmail(member.email);
      if (!emailKey) return;

      const current = membersByEmail.get(emailKey);
      if (!current) {
        membersByEmail.set(emailKey, member);
        return;
      }

      membersByEmail.set(emailKey, {
        ...current,
        ...member,
      });
    });

    pendingOrgInvites.forEach((invite) => {
      const emailKey = normalizeEmail(invite.email);
      if (!emailKey || membersByEmail.has(emailKey)) return;
      membersByEmail.set(emailKey, toPendingInviteMember(invite));
    });

    return Array.from(membersByEmail.values());
  }, [pendingOrgInvites, workspaceMembers]);

  const displayedMembers = useMemo<WorkspaceMember[]>(() => {
    const removedSet = new Set(optimisticRemovedEmails);

    const baseMembers = mergedMembers
      .filter((member) => {
        const emailKey = normalizeEmail(member.email);
        return !emailKey || !removedSet.has(emailKey);
      })
      .map((member) => {
        const emailKey = normalizeEmail(member.email);
        const overrideRole = optimisticRoleByEmail[emailKey];
        if (!overrideRole) return member;
        return { ...member, role_name: overrideRole };
      });

    const baseEmailSet = new Set(baseMembers.map((member) => normalizeEmail(member.email)).filter(Boolean));
    const optimisticOnly = optimisticInvites.filter((member) => {
      const emailKey = normalizeEmail(member.email);
      return !!emailKey && !removedSet.has(emailKey) && !baseEmailSet.has(emailKey);
    });

    return [...baseMembers, ...optimisticOnly];
  }, [mergedMembers, optimisticInvites, optimisticRemovedEmails, optimisticRoleByEmail]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return displayedMembers;
    return displayedMembers.filter((member) => {
      const name = `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim().toLowerCase();
      return name.includes(query) || member.email?.toLowerCase().includes(query);
    });
  }, [displayedMembers, search]);

  const pendingInvites = useMemo(
    () => displayedMembers.filter((member) => statusPill(member).label === "Pending").length,
    [displayedMembers],
  );

  const editableRolesForMember = useMemo(() => {
    if (!editingMember) return invitableRoles;
    return getAssignableRoles(currentUserRole);
  }, [currentUserRole, editingMember, invitableRoles]);

  const refetchMembers = useCallback(() => {
    if (workspace?.id) {
      void queryClient.invalidateQueries({ queryKey: ["workspace_members", workspace.id] });
    }
    void refreshOrgMembershipData();
  }, [refreshOrgMembershipData, workspace?.id]);

  const handleInvite = async () => {
    if (!workspace?.id || !orgId) return;

    const role = inviteRole.trim();
    if (!role) return;

    const { valid, invalid } = parseInviteEmails(inviteEmail);
    if (!valid.length) {
      notify({
        type: "failure",
        heading: "Invalid email addresses",
        messageList: ["Enter one or more valid email addresses."],
      });
      return;
    }

    const previousOptimisticInvites = [...optimisticInvites];
    const previousOptimisticRemoved = [...optimisticRemovedEmails];
    const validEmailKeys = new Set(valid.map((email) => normalizeEmail(email)));

    setOptimisticInvites((previous) => {
      const map = new Map(previous.map((member) => [normalizeEmail(member.email), member]));
      valid.forEach((email) => {
        const key = normalizeEmail(email);
        if (!key) return;
        map.set(key, toPendingInviteMember({ email, role }));
      });
      return Array.from(map.values());
    });
    setOptimisticRemovedEmails((previous) => previous.filter((key) => !validEmailKeys.has(key)));

    setLoading(true);
    try {
      const inviteResults = await Promise.allSettled(
        valid.map((email) => authApis.inviteUserToOrg({
          org_id: orgId,
          email,
          role,
        })),
      );

      const succeeded: string[] = [];
      const failed: string[] = [];

      inviteResults.forEach((result, index) => {
        const email = valid[index];
        if (result.status === "fulfilled" && result.value.ok) {
          succeeded.push(email);
        } else {
          failed.push(email);
        }
      });

      if (!succeeded.length) {
        setOptimisticInvites(previousOptimisticInvites);
        setOptimisticRemovedEmails(previousOptimisticRemoved);
        notify({
          type: "failure",
          heading: "Unable to invite members",
          messageList: ["Please verify the email list and try again."],
        });
        return;
      }

      if (failed.length > 0) {
        const failedSet = new Set(failed.map((email) => normalizeEmail(email)));
        setOptimisticInvites((previous) => previous.filter((member) => !failedSet.has(normalizeEmail(member.email))));
      }

      setPendingOrgInvites((previous) => {
        const seen = new Set(previous.map((invite) => normalizeEmail(invite.email)));
        const next = [...previous];
        succeeded.forEach((email) => {
          const key = normalizeEmail(email);
          if (!key || seen.has(key)) return;
          seen.add(key);
          next.push({ email, role });
        });
        return next;
      });

      const unsent = [...failed, ...invalid];
      if (unsent.length) {
        const messageParts: string[] = [];
        if (failed.length) {
          messageParts.push(`${failed.length} invite${failed.length > 1 ? "s" : ""} failed to send.`);
        }
        if (invalid.length) {
          messageParts.push(`${invalid.length} address${invalid.length > 1 ? "es were" : " was"} invalid.`);
        }
        notify({
          type: "failure",
          heading: "Some invites were not sent",
          messageList: [messageParts.join(" ")],
        });
        setInviteEmail(unsent.join(", "));
      } else {
        setInviteEmail("");
        setInviteRole("Member");
        setInviteOpen(false);
      }

      notify({
        type: "success",
        messageList: [`${succeeded.length} invite${succeeded.length > 1 ? "s" : ""} sent.`],
      });

      refetchMembers();
    } catch {
      setOptimisticInvites(previousOptimisticInvites);
      setOptimisticRemovedEmails(previousOptimisticRemoved);
      notify({
        type: "failure",
        heading: "Unable to invite members",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditRole = (member: WorkspaceMember) => {
    setEditingMember(member);
    setNewRole(member.role_name ?? null);
    setEditRoleOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!workspace?.id || !orgId || !editingMember || !newRole) return;

    const emailKey = normalizeEmail(editingMember.email);
    if (!emailKey) return;

    const userId = propelUserIdByEmail[emailKey];
    if (!userId) {
      notify({
        type: "failure",
        heading: "Unable to update role",
        messageList: ["User details are still syncing. Please try again in a few seconds."],
      });
      return;
    }

    const previousOverride = optimisticRoleByEmail[emailKey];
    setOptimisticRoleByEmail((previous) => ({ ...previous, [emailKey]: newRole }));

    setLoading(true);
    try {
      const response = await authApis.updateUserRoleInOrg({
        org_id: orgId,
        user_id: userId,
        role: newRole,
      });

      if (response.ok) {
        setOptimisticRoleByEmail((previous) => {
          const next = { ...previous };
          delete next[emailKey];
          return next;
        });
        notify({ type: "success", messageList: ["Role updated."] });
        setEditRoleOpen(false);
        setEditingMember(null);
        setNewRole(null);
        refetchMembers();
      } else {
        setOptimisticRoleByEmail((previous) => {
          const next = { ...previous };
          if (previousOverride) next[emailKey] = previousOverride;
          else delete next[emailKey];
          return next;
        });
        notify({
          type: "failure",
          heading: "Unable to update role",
          messageList: ["Please try again later."],
        });
      }
    } catch {
      setOptimisticRoleByEmail((previous) => {
        const next = { ...previous };
        if (previousOverride) next[emailKey] = previousOverride;
        else delete next[emailKey];
        return next;
      });
      notify({
        type: "failure",
        heading: "Unable to update role",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!workspace?.id || !orgId || !pendingRevokeEmail) return;

    const emailKey = normalizeEmail(pendingRevokeEmail);
    if (!emailKey) return;

    const previousRemovedEmails = [...optimisticRemovedEmails];
    const previousOptimisticInvites = [...optimisticInvites];

    setOptimisticRemovedEmails((previous) => (previous.includes(emailKey) ? previous : [...previous, emailKey]));
    setOptimisticInvites((previous) => previous.filter((member) => normalizeEmail(member.email) !== emailKey));

    setLoading(true);
    try {
      const response = await authApis.revokeUserOrgInvitation({
        org_id: orgId,
        email: pendingRevokeEmail,
      });

      if (response.ok) {
        setPendingOrgInvites((previous) => previous.filter((invite) => normalizeEmail(invite.email) !== emailKey));
        notify({ type: "success", messageList: ["Invite revoked."] });
        setRevokeInviteConfirmOpen(false);
        setPendingRevokeEmail(null);
        refetchMembers();
      } else {
        setOptimisticRemovedEmails(previousRemovedEmails);
        setOptimisticInvites(previousOptimisticInvites);
        notify({
          type: "failure",
          heading: "Unable to revoke invite",
          messageList: ["Please try again later."],
        });
      }
    } catch {
      setOptimisticRemovedEmails(previousRemovedEmails);
      setOptimisticInvites(previousOptimisticInvites);
      notify({
        type: "failure",
        heading: "Unable to revoke invite",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!workspace?.id || !orgId || !pendingRemoveMember) return;

    const emailKey = normalizeEmail(pendingRemoveMember.email);
    if (!emailKey) return;

    const userId = propelUserIdByEmail[emailKey];
    if (!userId) {
      notify({
        type: "failure",
        heading: "Unable to remove member",
        messageList: ["User details are still syncing. Please try again in a few seconds."],
      });
      return;
    }

    const previousRemovedEmails = [...optimisticRemovedEmails];
    setOptimisticRemovedEmails((previous) => (previous.includes(emailKey) ? previous : [...previous, emailKey]));

    setLoading(true);
    try {
      const response = await authApis.removeUserFromOrg({
        org_id: orgId,
        user_id: userId,
      });

      if (response.ok) {
        notify({ type: "success", messageList: ["Member removed."] });
        setRemoveMemberConfirmOpen(false);
        setPendingRemoveMember(null);
        refetchMembers();
      } else {
        setOptimisticRemovedEmails(previousRemovedEmails);
        notify({
          type: "failure",
          heading: "Unable to remove member",
          messageList: ["Please try again later."],
        });
      }
    } catch {
      setOptimisticRemovedEmails(previousRemovedEmails);
      notify({
        type: "failure",
        heading: "Unable to remove member",
        messageList: ["Please try again later."],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative space-y-6 ${embedded ? "" : "mx-auto max-w-6xl px-6 py-6"}`}>
      {(loading || isLoading) ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-foreground">Members</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {displayedMembers.length}
            {" "}
            members
            {" · "}
            {pendingInvites}
            {" "}
            pending
          </p>
        </div>
        {canInvite ? (
          <Button size="sm" className="h-8 text-[12px]" onClick={() => {
            setInviteRole(invitableRoles[invitableRoles.length - 1] ?? "Member");
            setInviteOpen(true);
          }}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Invite
          </Button>
        ) : null}
      </div>

      <div className="relative w-64 max-w-full">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search members..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="h-8 pl-8 text-[13px]"
        />
      </div>

      <div className="divide-y divide-border">
        {filteredMembers.map((member) => {
          const initials = getInitials(member);
          const role = member.role_name ?? "—";
          const status = statusPill(member);
          const joined = formatDate(member.joined_at);
          const displayName = `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() || member.email;
          const isPending = status.label === "Pending";
          const isSelf = normalizeEmail(member.email) === currentUserEmail;
          const canManage = !isSelf && canManageRole(currentUserRole, member.role_name);

          return (
            <div
              key={`${member.user_id ?? member.id ?? member.email}`}
              className="group flex items-center justify-between py-2.5"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-[13px] font-medium text-foreground">{displayName}</p>
                    {isSelf ? <span className="text-[11px] text-muted-foreground">(you)</span> : null}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">{member.email}</p>
                </div>
              </div>

              <div className="ml-4 flex items-center gap-4">
                <span className="text-[11px] text-muted-foreground">{role}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                  <span className="text-[11px] text-muted-foreground">{status.label}</span>
                </div>
                <span className="w-20 text-[11px] text-muted-foreground">{joined}</span>

                {canManage ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="h-7 w-7 rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-muted"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      {isPending ? (
                        <DropdownMenuItem
                          className="text-status-cancelled focus:text-status-cancelled"
                          onClick={() => {
                            setPendingRevokeEmail(member.email);
                            setRevokeInviteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Revoke invite
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem onClick={() => handleOpenEditRole(member)}>
                            <Pencil className="mr-2 h-3.5 w-3.5" />
                            Change role
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-status-cancelled focus:text-status-cancelled"
                            onClick={() => {
                              setPendingRemoveMember(member);
                              setRemoveMemberConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Remove member
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="h-7 w-7" />
                )}
              </div>
            </div>
          );
        })}

        {!filteredMembers.length ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No members found.</div>
        ) : null}
      </div>

      <Dialog
        open={inviteOpen}
        onOpenChange={(open) => {
          setInviteOpen(open);
          if (!open) {
            setInviteEmail("");
            setInviteRole("Member");
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Invite Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="invite-member-emails" className="text-[13px] text-foreground">
                Email addresses
              </Label>
              <Input
                id="invite-member-emails"
                placeholder="email@example.com, ..."
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                className="mt-1.5 text-[13px]"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">Comma-separated</p>
            </div>
            <div>
              <Label htmlFor="invite-member-role" className="text-[13px] text-foreground">
                Role
              </Label>
              <select
                id="invite-member-role"
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
                className="mt-1.5 h-9 w-full rounded-md border border-input bg-background px-3 text-[13px]"
              >
                {invitableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(false)} className="text-[12px]">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleInvite}
              disabled={loading || !inviteEmail.trim() || !inviteRole.trim()}
              className="text-[12px]"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Send Invites
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Change role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Update role for <span className="font-semibold text-foreground">{editingMember?.email}</span>
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="edit-member-role" className="text-[13px] text-foreground">
                Role
              </Label>
              <Select value={newRole ?? undefined} onValueChange={setNewRole}>
                <SelectTrigger id="edit-member-role" className="h-8 text-[13px]">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {editableRolesForMember.map((role) => (
                    <SelectItem key={role} value={role} className="text-[12px]">
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setEditRoleOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleUpdateRole} disabled={loading || !newRole}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Update role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revokeInviteConfirmOpen} onOpenChange={setRevokeInviteConfirmOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Revoke invite</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            Revoke the invite for <span className="font-semibold">{pendingRevokeEmail}</span>?
          </p>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setRevokeInviteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleRevokeInvite} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={removeMemberConfirmOpen} onOpenChange={setRemoveMemberConfirmOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Remove member</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            Remove <span className="font-semibold">{pendingRemoveMember?.email}</span> from this workspace?
          </p>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setRemoveMemberConfirmOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleRemoveMember} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkspaceMembersPage;
